import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Label } from "../../../../components/tailwind/label";
import { Input } from "../../../../components/tailwind/input";
import { loadTemplatesRequest } from "../../../../store/whatsappSlice";
import { TemplateSelector } from "../../../../components/whatsapp/send-message/TemplateSelector";
import { extractTemplateVariables } from "../../../../components/whatsapp/templates/TemplateUtils";
import { buildTemplateComponentsPayload } from "../../../../components/whatsapp/chat/templateMessageUtils";
import { workflowTheme } from "../../utils/workflowBuilderTheme";
import {
  parseTemplateParamsFromComponents,
  parseHeaderMediaLinkFromComponents,
  initializeTemplateParams,
  parseTemplateButtons,
} from "../../utils/workflowTemplateUtils";
import { WorkflowMediaField } from "./WorkflowMediaField";

const inspectorInputClass = `text-sm h-9 shadow-sm ${workflowTheme.input}`;

export function TemplateMessageConfig({
  value = {},
  onChange,
  paletteType,
  nodeId,
  saveSignal,
  uploadSilent,
  onPendingMediaChange,
  onRegisterPendingUploadNode
}) {
  const dispatch = useDispatch();
  const templates = useSelector((state) => state.whatsapp.templates) || [];

  useEffect(() => {
    dispatch(loadTemplatesRequest({ loadAll: true }));
  }, [dispatch]);

  const selectedTemplateId = useMemo(() => {
    if (!value?.templateId) return "";
    const found = templates.find(t => String(t.id) === String(value.templateId));
    return found ? found.id : value.templateId;
  }, [templates, value?.templateId]);

  const selectedTemplate = useMemo(() => {
    return templates.find(t => String(t.id) === String(selectedTemplateId));
  }, [templates, selectedTemplateId]);

  const isApproved = selectedTemplate && String(selectedTemplate.status || '').toLowerCase() === 'approved';

  const headerType = useMemo(() => {
    if (!selectedTemplate?.components?.HEADER) return 'none';
    try {
      const headerComponent = typeof selectedTemplate.components.HEADER === 'string'
        ? JSON.parse(selectedTemplate.components.HEADER)
        : selectedTemplate.components.HEADER;
      return String(headerComponent?.format || 'none').toLowerCase();
    } catch (e) {
      return selectedTemplate?.headerText ? 'text' : 'none';
    }
  }, [selectedTemplate]);

  const templateParams = useMemo(() => {
    if (value?.components && value.components.length > 0) {
      return parseTemplateParamsFromComponents(value.components, selectedTemplate);
    }
    if (value?.templateParams) {
      return value.templateParams;
    }
    return initializeTemplateParams(selectedTemplate);
  }, [value?.components, value?.templateParams, selectedTemplate]);

  const headerImageLink = useMemo(() => {
    if (value?.components && value.components.length > 0) {
      return parseHeaderMediaLinkFromComponents(value?.components, headerType);
    }
    return value?.headerImageLink ?? "";
  }, [value?.components, value?.headerImageLink, headerType]);

  const needsMediaUpload = ['image', 'video', 'document'].includes(headerType);

  const headerVariables = useMemo(() => {
    return extractTemplateVariables(selectedTemplate?.headerText);
  }, [selectedTemplate?.headerText]);

  const bodyVariables = useMemo(() => {
    return extractTemplateVariables(selectedTemplate?.body);
  }, [selectedTemplate?.body]);

  const buttonVariables = useMemo(() => {
    const btnList = parseTemplateButtons(selectedTemplate?.components?.BUTTONS);
    const vars = [];
    btnList.forEach((btn, buttonIndex) => {
      if (!btn) return;
      const url = btn.url || '';
      const matches = typeof url === 'string' ? (url.match(/\{\{\d+\}\}/g) || []) : [];
      matches.forEach((m, paramIndex) => {
        vars.push({
          buttonIndex: buttonIndex + 1,
          paramIndex: paramIndex + 1,
          key: `button_${buttonIndex}_${paramIndex}`
        });
      });
    });
    return vars;
  }, [selectedTemplate?.components?.BUTTONS]);

  const isAuthCopyCode = selectedTemplate?.category?.toLowerCase() === 'authentication' && 
                         (selectedTemplate?.otpType || 'copy_code') === 'copy_code';

  const updateConfigValue = (nextParams, nextMediaLink) => {
    if (!selectedTemplate) return;
    
    const params = nextParams || templateParams || {};
    const mediaLink = nextMediaLink !== undefined ? nextMediaLink : headerImageLink;
    
    const compiledComponents = buildTemplateComponentsPayload({
      template: selectedTemplate,
      headerValues: params.header || {},
      bodyValues: params.body || {},
      buttonValues: params.button || {},
      codeValue: params.code || "",
      isAuthCopyCode,
      headerMediaLink: mediaLink,
      buttonVariables
    });

    onChange({
      type: "template",
      templateId: selectedTemplate.id,
      name: selectedTemplate.name,
      language: selectedTemplate.language || "en",
      components: compiledComponents
    });
  };

  const handleTemplateSelect = (templateId) => {
    const template = templates.find(t => String(t.id) === String(templateId));
    if (!template) {
      onChange({
        type: "template",
        templateId: ""
      });
      return;
    }
    
    const initialParams = initializeTemplateParams(template);
    
    const isAuth = template.category?.toLowerCase() === 'authentication' && (template.otpType || 'copy_code') === 'copy_code';
    
    const btnList = parseTemplateButtons(template.components?.BUTTONS);
    const btnVars = [];
    btnList.forEach((btn, buttonIndex) => {
      if (!btn) return;
      const url = btn.url || '';
      const matches = typeof url === 'string' ? (url.match(/\{\{\d+\}\}/g) || []) : [];
      matches.forEach((m, paramIndex) => {
        btnVars.push({
          buttonIndex: buttonIndex + 1,
          paramIndex: paramIndex + 1,
          key: `button_${buttonIndex}_${paramIndex}`
        });
      });
    });

    const compiledComponents = buildTemplateComponentsPayload({
      template,
      headerValues: initialParams.header || {},
      bodyValues: initialParams.body || {},
      buttonValues: initialParams.button || {},
      codeValue: initialParams.code || "",
      isAuthCopyCode: isAuth,
      headerMediaLink: "",
      buttonVariables: btnVars
    });

    onChange({
      type: "template",
      templateId: template.id,
      name: template.name,
      language: template.language || "en",
      components: compiledComponents
    });
  };

  const renderManualInputField = (label, varName, sectionKey, valueKey) => (
    <div key={valueKey} className="space-y-1">
      <Label className="text-xs font-semibold text-gray-600 dark:text-gray-400">
        {label} <span className="text-red-500">*</span>
      </Label>
      <Input
        type="text"
        className={inspectorInputClass}
        value={sectionKey ? (templateParams?.[sectionKey]?.[valueKey] || '') : (templateParams?.[valueKey] || '')}
        onChange={(e) => {
          const newParams = { ...templateParams };
          if (sectionKey) {
            newParams[sectionKey] = {
              ...(newParams[sectionKey] || {}),
              [valueKey]: e.target.value
            };
          } else {
            newParams[valueKey] = e.target.value;
          }
          updateConfigValue(newParams);
        }}
      />
    </div>
  );

  const getFormattedPreview = () => {
    if (!selectedTemplate) return { header: "", body: "", footer: "" };
    
    const replaceVars = (text, type) => {
      if (!text) return "";
      return text.replace(/\{\{\s*([^{}]+?)\s*\}\}/g, (match, name) => {
        const key = name.trim();
        const val = templateParams?.[type]?.[key];
        return val ? `*${val}*` : match;
      });
    };

    const header = replaceVars(selectedTemplate.headerText, 'header');
    const body = replaceVars(selectedTemplate.body, 'body');
    const footer = selectedTemplate.footerText || "";
    
    return { header, body, footer };
  };

  const { header, body, footer } = getFormattedPreview();

  const parsedButtons = useMemo(() => {
    return parseTemplateButtons(selectedTemplate?.components?.BUTTONS);
  }, [selectedTemplate?.components?.BUTTONS]);

  const renderFormattedText = (text) => {
    if (!text) return null;
    const parts = text.split(/(\*[^*]+\*)/g);
    return parts.map((part, index) => {
      if (part.startsWith("*") && part.endsWith("*")) {
        return <strong key={index} className="font-semibold text-gray-900 dark:text-white">{part.slice(1, -1)}</strong>;
      }
      return part;
    });
  };

  return (
    <div className="space-y-4">
      <style dangerouslySetInnerHTML={{ __html: `
        .workflow-template-inspector-selector > div {
          padding: 0 !important;
          background: transparent !important;
          border: none !important;
          box-shadow: none !important;
        }
        .workflow-template-inspector-selector > div > div:first-child {
          display: none !important;
        }
        .workflow-template-inspector-selector button[role="combobox"] {
          height: 2.25rem !important;
          border-width: 1px !important;
          border-radius: 0.375rem !important;
          font-size: 0.875rem !important;
          padding-left: 0.75rem !important;
          padding-right: 0.75rem !important;
          box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05) !important;
          background-color: #f9fafb !important;
          border-color: #e5e7eb !important;
          color: #111827 !important;
          transition: border-color 0.2s, box-shadow 0.2s;
        }
        .workflow-template-inspector-selector button[role="combobox"]:hover {
          border-color: #cbd5e1 !important;
        }
        .dark .workflow-template-inspector-selector button[role="combobox"] {
          background-color: #020617 !important;
          border-color: #1e293b !important;
          color: #f9fafb !important;
        }
        .dark .workflow-template-inspector-selector button[role="combobox"]:hover {
          border-color: #334155 !important;
        }
      `}} />

      <div className="space-y-1">
        <Label className="text-xs font-semibold text-gray-600 dark:text-gray-400">
          Template Name <span className="text-red-500">*</span>
        </Label>
        <div className="workflow-template-inspector-selector">
          <TemplateSelector
            templates={templates}
            selectedId={selectedTemplateId}
            onSelect={handleTemplateSelect}
            hideStepNumber={true}
          />
        </div>
      </div>

      {selectedTemplateId && selectedTemplate && !isApproved && (
        <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-xs text-amber-700 dark:text-amber-400 font-medium">
          ⚠️ Cannot use template. Your template is currently {selectedTemplate.status?.toLowerCase() || 'pending'}. Templates must be approved before they can be used.
        </div>
      )}

      {selectedTemplateId && selectedTemplate && (
        <div className="space-y-4 pt-4 border-t border-gray-100 dark:border-slate-800">
          {needsMediaUpload && (
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                Header Media File <span className="text-red-500">*</span>
              </Label>
              <WorkflowMediaField
                value={headerImageLink}
                filename=""
                mediaType={headerType}
                nodeId={nodeId}
                saveSignal={saveSignal}
                uploadSilent={uploadSilent}
                onPendingChange={onPendingMediaChange}
                onRegisterPendingUploadNode={onRegisterPendingUploadNode}
                onChange={(nextValue, meta) => {
                  updateConfigValue(undefined, nextValue);
                }}
              />
            </div>
          )}

          {(headerVariables.length > 0 || bodyVariables.length > 0 || buttonVariables.length > 0 || isAuthCopyCode) && (
            <div className="space-y-3">
              <h4 className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Template Variables</h4>
              {headerVariables.map((v) => renderManualInputField(`Header Variable: {{${v}}}`, v, 'header', v))}
              {bodyVariables.map((v) => renderManualInputField(`Body Variable: {{${v}}}`, v, 'body', v))}
              {buttonVariables.map((v) => renderManualInputField(`Button ${v.buttonIndex} URL Variable: {{${v.paramIndex}}}`, v.key, 'button', v.key))}
              {isAuthCopyCode && renderManualInputField(`Authentication Code`, 'code', null, 'code')}
            </div>
          )}

          <div className="mt-4 p-3 bg-[#efeae2] dark:bg-[#0b141a] rounded-xl border border-gray-200 dark:border-slate-800 space-y-2">
            <div className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
              Live Message Preview
            </div>
            <div className="flex flex-col">
              <div className="bg-white dark:bg-[#1f2c34] rounded-lg rounded-tl-none shadow-sm border border-gray-100 dark:border-slate-800/80 max-w-[90%] overflow-hidden relative self-start">
                {needsMediaUpload && (
                  <div className="p-1.5 pb-0">
                    {headerType === 'image' && headerImageLink ? (
                      <img src={headerImageLink} alt="Header" className="w-full h-28 object-cover rounded" />
                    ) : (
                      <div className="w-full h-12 bg-gray-50 dark:bg-slate-900 rounded flex items-center justify-center gap-2 text-gray-400 text-xs font-medium border border-dashed border-gray-200 dark:border-slate-800">
                        <span>Attachment ({headerType})</span>
                      </div>
                    )}
                  </div>
                )}
                
                <div className="p-2.5 pb-1.5 px-3 space-y-1">
                  {header && !needsMediaUpload && (
                    <p className="text-[13px] font-bold text-gray-900 dark:text-white">{renderFormattedText(header)}</p>
                  )}
                  <p className="text-[13px] text-gray-800 dark:text-slate-200 whitespace-pre-wrap leading-relaxed">{renderFormattedText(body)}</p>
                  {footer && (
                    <p className="text-[10px] text-gray-400 dark:text-slate-400 mt-1">{footer}</p>
                  )}
                </div>

                {parsedButtons.map((btn, idx) => (
                  <div key={idx} className="border-t border-gray-100 dark:border-slate-800/60 py-2 text-center text-xs font-semibold text-sky-600 dark:text-sky-400 cursor-default hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                    {btn.text}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
